/**
 * App Layout — top nav bar with user info, balance, and logout
 */

import { Link } from 'react-router-dom';
import { HELP_URL } from '../lib/legalUrls';
import { useProfile, useAuthStore } from '../store/useAuthStore';
import { APP_VERSION } from '../config/constants';
import { Tooltip } from './ui/Tooltip';
import { ToastContainer } from './Toast';
import PaygFloorBanner from './PaygFloorBanner';

function formatBalance(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function planLabel(plan: string): string {
  switch (plan) {
    case 'payg': return 'PAYG';
    case 'pro': return 'Pro';
    case 'team': return 'Team';
    case 'enterprise': return 'Enterprise';
    default: return 'Free';
  }
}

function planBadgeClass(plan: string): string {
  switch (plan) {
    case 'pro': return 'bg-[#00d4b8]/10 text-[#007a6d] border border-[#00d4b8]/30';
    case 'payg': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'team':
    case 'enterprise': return 'bg-violet-50 text-violet-700 border border-violet-200';
    default: return 'bg-gray-100 text-gray-500 border border-gray-200';
  }
}

// The payment modal (mounted once in main.tsx) listens for this event — the one way in.
function openTopUp() {
  window.dispatchEvent(new CustomEvent('lp:open-top-up'));
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const profile = useProfile();
  const { signOut } = useAuthStore();
  const isOrgPlan = profile?.plan === 'team' || profile?.plan === 'enterprise';
  const lowBalance = !isOrgPlan && (profile?.balance_seconds ?? 0) < 300; // <5 min

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Nav bar */}
      <nav className="bg-white/95 border-b border-gray-200 backdrop-blur px-4 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Left: Logo */}
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-gray-900">
              Linguist<span className="text-[#007a6d]">Pro</span>
            </h1>
            <span className="text-xs text-gray-400">{APP_VERSION}</span>
          </div>

          {/* Right: User info */}
          <div className="flex items-center flex-wrap gap-2 sm:gap-4">
            {profile && (
              <>
                {/* Balance */}
                {profile.plan === 'pro' ? (
                  <Tooltip text="Pro plan — unlimited transcription">
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-[#00d4b8]/10 text-[#007a6d] border border-[#00d4b8]/30">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Unlimited
                    </div>
                  </Tooltip>
                ) : isOrgPlan ? (
                  <Tooltip text="Pooled organisation balance — see Organisation page for details">
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-violet-50 text-violet-700 border border-violet-200">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Pooled
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip text="Your remaining transcription time">
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                      lowBalance
                        ? 'bg-amber-50 text-amber-700 border border-amber-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {formatBalance(profile.balance_seconds)}
                    </div>
                  </Tooltip>
                )}

                {/* Top Up button */}
                {profile.plan !== 'pro' && !isOrgPlan && (
                  <Tooltip text="Buy more transcription time">
                    <button
                      onClick={openTopUp}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00d4b8] text-[#0d1117] hover:bg-[#00bfa8] transition-colors"
                    >
                      Top Up
                    </button>
                  </Tooltip>
                )}

                {/* Plan badge */}
                <Tooltip text={`Your current plan: ${profile.plan === 'payg' ? 'Pay-As-You-Go' : profile.plan === 'pro' ? 'Pro (unlimited)' : 'Free (30 min included)'}`}>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${planBadgeClass(profile.plan)}`}>
                    {planLabel(profile.plan)}
                  </span>
                </Tooltip>

                {/* User name */}
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {profile.display_name || profile.email}
                </span>
              </>
            )}

            {/* Profile link */}
            <Tooltip text="Your profile, wallet & history">
              <Link
                to="/profile"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Profile
              </Link>
            </Tooltip>

            {/* History link */}
            <Tooltip text="Transcription history">
              <Link
                to="/transcriptions"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                History
              </Link>
            </Tooltip>

            {/* Organisation link — only for team/enterprise members */}
            {isOrgPlan && (
              <Tooltip text="Organisation dashboard — members, balance, usage">
                <Link
                  to="/org"
                  className="text-sm text-violet-600 hover:text-violet-800 font-medium transition-colors"
                >
                  Organisation
                </Link>
              </Tooltip>
            )}

            {/* Settings link */}
            <Tooltip text="Account settings">
              <Link
                to="/settings"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Settings
              </Link>
            </Tooltip>

            {/* Help link */}
            <Tooltip text="Help & Documentation">
              <a
                href={HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Help
              </a>
            </Tooltip>

            {/* Logout */}
            <button
              onClick={signOut}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* One-time pay-as-you-go minimum notice */}
        <PaygFloorBanner />

        {/* Low balance banner */}
        {lowBalance && !isOrgPlan && profile && profile.balance_seconds > 0 && (
          <div className="max-w-7xl mx-auto mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center justify-between">
            <span>Low balance: {formatBalance(profile.balance_seconds)} remaining.</span>
            {profile.plan !== 'pro' && (
              <button
                onClick={openTopUp}
                className="text-xs font-semibold text-amber-700 underline hover:text-amber-900"
              >
                Top up now
              </button>
            )}
          </div>
        )}
      </nav>

      {/* Content */}
      <main>{children}</main>

      {/* Toast notifications */}
      <ToastContainer />
    </div>
  );
}
